import type {
  FaireAcademyChapter,
  FaireAcademyGuide,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/types";
import { convertTitleToHash } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/utils";
import { IFaireAcademyChapter } from "@faire/web-api--source/indigofair/data/IFaireAcademyChapter";
import { IFaireAcademyGuide } from "@faire/web-api--source/indigofair/data/IFaireAcademyGuide";
import organizeStoreOperations from "@faire/web-api--source/routes/www/open-with-faire/academy/organize-store-operations";
import { lazy } from "react";
import * as React from "react";

const LazyPointOfSaleSystem = lazy(() =>
  import(
    /* webpackChunkName: "organize-store-operations-pos-system" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/OrganizeStoreOperations/__internal__/PointOfSaleSystems"
  ).then((m) => ({ default: m.PointOfSaleSystem }))
);
const LazyInsurance = lazy(() =>
  import(
    /* webpackChunkName: "organize-store-operations-insurance" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/OrganizeStoreOperations/__internal__/Insurance"
  ).then((m) => ({ default: m.Insurance }))
);
const LazyStaffing = lazy(() =>
  import(
    /* webpackChunkName: "organize-store-operations-staffing" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/OrganizeStoreOperations/__internal__/Staffing"
  ).then((m) => ({ default: m.Staffing }))
);
const LazyPayroll = lazy(() =>
  import(
    /* webpackChunkName: "organize-store-operations-payroll" */
    "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/guideData/OrganizeStoreOperations/__internal__/Payroll"
  ).then((m) => ({ default: m.Payroll }))
);

const C1 = "Point of sale (POS) systems";
const POS_SYSTEMS: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.POINT_OF_SALE_SYSTEMS,
  title: C1,
  readTimeMinutes: 7,
  titleHash: convertTitleToHash(C1),
  ChapterComponent: LazyPointOfSaleSystem,
};

const C2 = "Insurance";
const INSURANCE: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.INSURANCE,
  title: C2,
  readTimeMinutes: 3,
  titleHash: convertTitleToHash(C2),
  ChapterComponent: LazyInsurance,
};

const C3 = "Staffing";
const STAFFING: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.STAFFING,
  title: C3,
  readTimeMinutes: 6,
  titleHash: convertTitleToHash(C3),
  ChapterComponent: LazyStaffing,
};

const C4 = "Payroll";
const PAYROLL: FaireAcademyChapter = {
  id: IFaireAcademyChapter.ChapterIdentifier.PAYROLL,
  title: C4,
  readTimeMinutes: 1,
  titleHash: convertTitleToHash(C4),
  ChapterComponent: LazyPayroll,
};

const GUIDE_CHAPTERS: Array<FaireAcademyChapter> = [
  POS_SYSTEMS,
  INSURANCE,
  STAFFING,
  PAYROLL,
];

export const ORGANIZE_STORE_OPERATIONS: FaireAcademyGuide = {
  id: IFaireAcademyGuide.GuideIdentifier.ORGANIZE_YOUR_OPERATIONS,
  title: "Organize your operations",
  description:
    "Get the right point of sale, insurance, and staffing resources.",
  cardSummary: "Get the right tools in place so your business runs smoothly.",
  guideSummary: (
    <>
      Efficient operations are the backbone of any successful retail store.
      Choosing a point of sale system, such as Shopify, is just the first step,
      and there are a handful of options. Once you decide on a system, you’ll
      want to get it configured and keep it up-to-date. You’ll also need various
      insurance policies to protect your business and assets against risks you
      wouldn’t be able to afford on your own. Of course, no store can succeed
      without a small but mighty, enthusiastic team. Cultivating your team
      involves curation, connection, and economic opportunity—just like starting
      your business.
    </>
  ),
  featuredImage:
    "https://cdn.faire.com/fastly/bf2997f3fcbeaa88274e8cc2f0c39d46ad550136b3ceb9c08b6cbeae66d30e40.png",
  featuredImageAltText: "ZIZIA brand owner standing in front of product shelf",
  backgroundImage:
    "https://cdn.faire.com/fastly/ec544053e4a5ae8ae8ad0ccb3b5ccfc95141f4f43c489e20a13c66e94e498a34.png",
  readTimeMinutes: GUIDE_CHAPTERS.reduce(
    (partialReadTime, chapter) => partialReadTime + chapter.readTimeMinutes,
    0
  ),
  chapters: GUIDE_CHAPTERS,
  linkToPage: organizeStoreOperations(),
  titleTag: "How to staff, insure & resource your retail store",
};
